import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { useProductPicker } from "@/modules/products/ProductPicker";
import { useRolloutRules, useToolReleases, type RolloutRule, type ToolRelease } from "@/modules/products/fixtures";
import { SoonButton } from "@/components/SoonButton";

const RELEASE_STATUS: StatusMap = {
  published: { label: "PUBLISHED", color: "var(--ok)" },
  draft: { label: "DRAFT", color: "var(--warn)" },
  archived: { label: "ARCHIVED", color: "var(--t-mute)" },
};

const RELEASE_COLS: DataCol<ToolRelease>[] = [
  { key: "ver", label: "Version", width: 130, mono: true, render: (r) => r.version_name },
  { key: "code", label: "Code", width: 80, mono: true, render: (r) => r.version_code },
  {
    key: "chan", label: "Channel", width: 90,
    render: (r) => <Chip tone={r.channel === "stable" ? "ok" : "warn"}>{r.channel}</Chip>,
  },
  { key: "size", label: "Size", width: 90, render: (r) => `${(r.size_bytes / 1_048_576).toFixed(0)} MB` },
  {
    key: "sha", label: "SHA-256", width: 130, mono: true,
    title: () => "Returned to the client so it can verify the installer BEFORE running it",
    render: (r) => r.sha256,
  },
  { key: "status", label: "Status", width: 110, render: (r) => <StatusChip st={r.status} map={RELEASE_STATUS} /> },
  { key: "notes", label: "Notes", width: 200, render: (r) => r.notes ?? <span className="cap">—</span> },
  {
    key: "act", label: "", width: 210,
    render: (r) => (
      <span style={{ display: "flex", gap: 6 }}>
        <SoonButton className="btn ghost sm" why="fetches the stored file">Download</SoonButton>
        {r.status === "draft" && <SoonButton className="btn ghost sm" why="makes a draft release available to its audience">Publish</SoonButton>}
        {r.status === "published" && <SoonButton className="btn ghost sm" why="stops offering a published release">Withdraw</SoonButton>}
      </span>
    ),
  },
];

const RULE_COLS: DataCol<RolloutRule>[] = [
  {
    key: "scope", label: "Audience", width: 250,
    render: (r) => r.scope_type === "global"
      ? <Chip tone="info">everyone</Chip>
      : <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Chip tone="mute">{r.scope_type}</Chip>
          <span className="mono">{r.scope_value}</span>
        </span>,
  },
  { key: "offer", label: "Offers", width: 90, mono: true, render: (r) => r.offer_version ?? "—" },
  {
    key: "floor", label: "Floor", width: 110, mono: true,
    title: () => "Below this version the client MUST update — the update stops being optional",
    render: (r) => r.floor_version
      ? <span style={{ color: "var(--bad)" }}>{r.floor_version}</span>
      : <span className="cap">none</span>,
  },
  {
    key: "on", label: "Enabled", width: 90,
    render: (r) => r.enabled ? <Chip tone="ok">on</Chip> : <Chip tone="mute">off</Chip>,
  },
  { key: "act", label: "", width: 120, render: () => <SoonButton className="btn ghost sm" why="removes a space that has no allocations">Delete</SoonButton> },
];

/** Tool updates — installers, and who is offered which.
 *
 *  This is the pattern worth noticing: a release carries a comparable
 *  `version_code`, a channel, and a **sha256 the client verifies before it runs
 *  the installer**, and the audience is RE-RESOLVED server-side at download so
 *  nobody can pull a build by guessing a URL. The firmware that goes into a
 *  vehicle has none of that today — same codebase, the careful pattern pointed
 *  at the laptop rather than the product.
 */
export function ToolUpdatesPage() {
  const { product, picker } = useProductPicker();
  const releases = useToolReleases(product.product_id);
  const rules = useRolloutRules(product.product_id);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Tool updates"
        title="Tool updates"
        desc="Installers, and who is offered which. The most specific enabled rule wins; a floor makes the update mandatory."
        right={picker}
      />

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SoonButton className="btn teal sm" why="uploads a build and records its sha256">Upload installer</SoonButton>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
          uploaded in chunks — a build is hundreds of MB
        </span>
      </div>

      <div className="section-label">Releases</div>
      <DataTable rows={releases} cols={RELEASE_COLS} rowKey={(r) => r.release_id}
        empty={<div className="panel panel-pad cap">No installers uploaded for this product.</div>} />

      <div className="section-label tight">Rollout rules</div>
      <div className="cap" style={{ marginBottom: 8 }}>
        The most specific <strong>enabled</strong> rule wins: user beats company beats role beats everyone.
        A <strong>floor</strong> makes the update mandatory below that version — it is how an old bench stops
        quietly running last quarter&rsquo;s build.
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <SoonButton className="btn ghost sm" why="targets a release at an audience">Add rule</SoonButton>
      </div>
      <DataTable rows={rules} cols={RULE_COLS} rowKey={(r) => r.rule_id}
        empty={<div className="panel panel-pad cap">No rules — nobody is offered an update.</div>} />
    </div>
  );
}
